import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  FileText, 
  Printer,
} from 'lucide-react';
import { useInvoices, useDeleteInvoice, useGenerateInvoicePDF } from '../../hooks/useInvoices';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export function InvoiceList() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const { data, isLoading, isError, refetch } = useInvoices({ search, status });
  const deleteInvoice = useDeleteInvoice();
  const generatePDF = useGenerateInvoicePDF();

  const getStatusBadge = (estado: string) => {
    const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
      pendiente: { variant: 'warning', label: 'Pendiente' },
      pagada: { variant: 'success', label: 'Pagada' },
      cancelada: { variant: 'danger', label: 'Cancelada' },
    };
    const config = variants[estado] || variants.pendiente;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleDelete = async (id: number, cliente: string) => {
    if (!confirm(`¿Estás seguro de eliminar la factura de "${cliente}"?`)) return;

    try {
      await deleteInvoice.mutateAsync(id);
      toast.success('Factura eliminada exitosamente');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar la factura');
    }
  };

  const handleGeneratePDF = async (id: number, cliente: string) => {
    try {
      await generatePDF.mutateAsync(id);
      toast.success(`PDF generado para "${cliente}"`);
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al generar el PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando facturas...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar las facturas
      </div>
    );
  }

  const facturas = data || [];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Facturas Prepago</h1>
          <p className="text-sm text-gray-500">
            {facturas.length} factura(s) registradas
          </p>
        </div>
        <Link to="/facturas/nuevo">
          <Button>
            <Plus size={18} />
            Nueva Factura
          </Button>
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            type="text"
            placeholder="Buscar por cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="pagada">Pagada</option>
          <option value="cancelada">Cancelada</option>
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Cliente</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Productos</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Total</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Fecha</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {facturas.map((factura: any) => {
                const productos = JSON.parse(factura.productosJson || '[]');
                const totalProductos = productos.reduce((sum: number, p: any) => sum + (p.cantidad || 1), 0);

                return (
                  <tr key={factura.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{factura.clienteNombre}</p>
                      {factura.clienteDocumento && (
                        <p className="text-xs text-gray-400">Doc: {factura.clienteDocumento}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {productos.length} productos ({totalProductos} unidades)
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">
                      {formatCurrency(factura.total || 0)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(factura.estado)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-500">
                      {formatDate(factura.fechaEmision)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-1">
                        <Link
                          to={`/facturas/${factura.id}`}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                          title="Ver"
                        >
                          <Eye size={16} />
                        </Link>
                        <Link
                          to={`/facturas/editar/${factura.id}`}
                          className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </Link>
                        <button
                          onClick={() => handleGeneratePDF(factura.id, factura.clienteNombre)}
                          className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors"
                          title="Generar PDF"
                          disabled={factura.estado === 'cancelada'}
                        >
                          <Printer size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(factura.id, factura.clienteNombre)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {facturas.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron facturas</p>
          <Link to="/facturas/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Crear la primera factura
          </Link>
        </div>
      )}
    </div>
  );
}